import { useCallback, useContext } from 'react';
import {
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
} from 'react-router-dom';
import { useTabContextSafe } from '../contexts/TabContext';
import {
  buildNoteRoute,
  buildNewNoteRoute,
  getReturnContextFromLocation,
} from '../utils/noteReturnContext';

/**
 * Centralized navigation helper hook.
 *
 * Wraps React Router's `navigate()` so every in-app route change goes through
 * the router instead of `window.location.href` (which causes a full page
 * reload and breaks the internal tab system).
 *
 * Usage:
 *   const nav = useAppNavigation();
 *   nav.toNote('abc-123');
 *   nav.toNewNote({ appointmentId: '...', patientId: '...' });
 *   nav.go('/some/path');
 *
 * For auth redirects or external URLs, continue using window.location.href
 * directly — this hook is only for internal app routes.
 */
export function useAppNavigation() {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const locationContext = useContext(UNSAFE_LocationContext);
  const location = locationContext?.location ?? window.location;
  const tabContext = useTabContextSafe();
  const noteReturnContext = getReturnContextFromLocation(location);
  const navigate = useCallback(
    (path: string) => {
      if (navigationContext) {
        navigationContext.navigator.push(path);
        return;
      }

      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
    [navigationContext],
  );

  /** Navigate to an arbitrary internal path. */
  const go = useCallback(
    (path: string, title = '') => {
      tabContext?.openTab(path, title, null);
      navigate(path);
    },
    [navigate, tabContext],
  );

  /** Open an existing note by ID. */
  const toNote = useCallback(
    (noteId: string) => {
      go(buildNoteRoute(noteId, noteReturnContext), 'Note');
    },
    [go, noteReturnContext],
  );

  /** Open the new-note editor, optionally pre-filled with appointment/patient. */
  const toNewNote = useCallback(
    ({ appointmentId, patientId, caseId }: { appointmentId: string; patientId?: string; caseId?: string }) => {
      go(buildNewNoteRoute({ appointmentId, patientId, caseId }, noteReturnContext), 'New Note');
    },
    [go, noteReturnContext],
  );



  /** Navigate to the AI assistant, optionally in a guided mode. */
  const toAiAssistant = useCallback(
    (mode?: 'guided_today' | 'guided_pending') => {
      go(mode ? `/ai-assistant?mode=${mode}` : '/ai-assistant', 'AI Assistant');
    },
    [go],
  );

  /** Navigate to a specific client's chart. */
  const toClient = useCallback(
    (patientId: string) => {
      navigate(`/clients/${encodeURIComponent(patientId)}`);
    },
    [navigate],
  );

  /** Navigate to payments, optionally scoped to a patient + appointment. */
  const toPayments = useCallback(
    (opts?: { patientId?: string; appointmentId?: string }) => {
      if (opts?.patientId) {
        const params = new URLSearchParams({ patient_id: opts.patientId });
        if (opts.appointmentId) params.set('appointment_id', opts.appointmentId);
        navigate(`/billing/payments?${params.toString()}`);
      } else {
        navigate('/billing/payments');
      }
    },
    [navigate],
  );

  /** Navigate to invoices scoped to a patient. */
  const toInvoices = useCallback(
    (patientId: string) => {
      navigate(`/billing/invoices?patient_id=${encodeURIComponent(patientId)}`);
    },
    [navigate],
  );

  /** Navigate to the calendar, optionally focusing on an appointment. */
  const toCalendar = useCallback(
    (appointmentId?: string) => {
      navigate(appointmentId ? `/calendar?encounter=${encodeURIComponent(appointmentId)}` : '/calendar');
    },
    [navigate],
  );

  /** Navigate to the dashboard. */
  const toDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  /** Navigate to settings, optionally with a hash fragment. */
  const toSettings = useCallback(
    (hash?: string) => {
      navigate(`/settings${hash ? `#${hash}` : ''}`);
    },
    [navigate],
  );

  /** Navigate to the admin setup wizard, optionally at a step. */
  const toSetupWizard = useCallback(
    (step?: string) => {
      const params = step ? `?step=${encodeURIComponent(step)}` : '';
      navigate(`/admin/setup-wizard${params}`);
    },
    [navigate],
  );

  /** Navigate to admin settings subscription tab. */
  const toSubscriptionSettings = useCallback(() => {
    navigate('/admin/settings?tab=subscription');
  }, [navigate]);

  return {
    go,
    toNote,
    toNewNote,
    toAiAssistant,
    toClient,
    toPayments,
    toInvoices,
    toCalendar,
    toDashboard,
    toSettings,
    toSetupWizard,
    toSubscriptionSettings,
  };
}

export default useAppNavigation;
